import { type CollectionSummaryJSONObject, IkeToken, type TagJSONObject } from '@ikegps/core-api';
import { getLiveAssetType } from '../../components/collection/live.asset.type';
import { getShowRedDot } from '../../components/red.dot/is.missing.true.size';
import { addStyles } from '../../components/theme';
import { DRAGON_ID, DRAGON_TYPE, DragonDomTypes } from '../../dom';
import { T, Translate } from '../../i18n/index';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { TagState } from '../../page/tag.state';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { TagSortState } from '../../state/tag.sort.state';
import { TagIcon } from '../tag.icon/tag.icon';
import { CSS as CollectionCardCSS } from './collection.card.style';
import type { VariantRow } from './collection.variant.group';
import { CSS, Style } from './variant.card.style';

addStyles(Style);

/**
 * A single read-only variant row, indented under its parent pole. Shows the same status icon as a
 * job-list collection (map-marker colour, red dot for missing true size) — but never the selection
 * checkbox, since individual variants are not selectable. Clicking opens the variant.
 */
export const VariantCard = {
	view(
		state: TagState,
		tag: TagJSONObject,
		row: VariantRow<CollectionSummaryJSONObject>,
		parentRevision: string
	): MithrilVNode {
		const label = VariantCard.label(row);
		const color = Services.Token.has(IkeToken.FEATURE_COLOR_FLAG) ? row.summary.mapMarkerColor : null;
		const children: MithrilVNode[] = [
			TagIcon.view(tag, undefined, color, getShowRedDot(row.summary), getLiveAssetType(row.summary)),
			m('span', { className: CSS.label, title: label }, label),
		];

		// Mirror the job-list card's info column so each variant row carries the same detail (a timestamp
		// under the default "last updated" sort) as its parent and siblings, instead of showing nothing.
		let displayValue = TagSortState.getCollectionDisplayValue(row.summary);
		if (displayValue != null) {
			if (displayValue instanceof Date) {
				displayValue = Services.DateTime.formatDateTime(displayValue);
			}
			children.push(
				m('div', { className: CollectionCardCSS.info, title: Translate(TagSortState.option.title) }, displayValue)
			);
		}

		return m('div', VariantCard.getAttributes(state, row.summary, parentRevision), children);
	},

	label(row: VariantRow<CollectionSummaryJSONObject>): string {
		if (row.isExisting) {
			return Translate(T.VARIANT_TYPE_EXISTING);
		}
		return row.variantTypeName ?? row.variantType;
	},

	getAttributes(state: TagState, summary: CollectionSummaryJSONObject, parentRevision: string) {
		// Hovering a variant row highlights its parent pole's map marker (a variant has no marker of its
		// own, UFO-1215), the same way hovering a collection name does. The `c-id` (DRAGON_ID) is the
		// parent's revision, so dom.ts' hover handler lights up the parent's point. The rows keep their CSS
		// :hover for per-item feedback and do not take the revision-keyed row highlight, which would
		// otherwise light up every row that shares the parent's revision.
		const isInspected =
			CurrentState.inspectedCollectionRevision === summary.revision ? CollectionCardCSS.isInspected : '';
		return {
			key: 'vcard-' + summary.revision,
			className: `${CollectionCardCSS.container} ${CSS.row} ${isInspected}`,
			[DRAGON_ID]: parentRevision,
			[DRAGON_TYPE]: DragonDomTypes.COLLECTION,
			onclick: () => state.map.zoomToCollection(summary.revision),
		};
	},
};
